import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useBusinessUnit } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { IntegrationSheet } from './integration-sheet';
import type { CatalogIntegration } from '../types';
import { isAccountScopedIntegration, isKimHelpdesk } from '../provider-kind';

interface IntegrationCardProps {
  integration: CatalogIntegration;
  /** The account's connected primary helpdesk, if any; an account has one primary helpdesk. */
  connectedHelpdesk?: string;
}

export const IntegrationCard = ({ integration, connectedHelpdesk }: IntegrationCardProps) => {
  const navigate = useNavigate();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const { buId, businessUnits } = useBusinessUnit();

  // Connecting writes a row that belongs to exactly one brand, so with "All business
  // units" selected on a multi-brand account there is nothing to write it to. machine
  // would file it under the account's default — which is a real answer, and the wrong
  // one to give silently when the operator never named a brand. Ask first.
  //
  // Managing an EXISTING connection is unaffected: that row already names its brand.
  // A helpdesk connection is the account's, so "which brand does this belong to" is not a
  // question worth blocking on — connecting one with no brand selected is correct.
  const needsBusinessUnit =
    !isAccountScopedIntegration(integration.catalogId) &&
    !integration.isConnected &&
    buId === null &&
    businessUnits.length > 1;

  // One primary helpdesk serves the whole account, so a second one is offered only once the first
  // is disconnected. Kim Helpdesk is the secondary and is never blocked by the primary.
  const helpdeskTaken =
    isAccountScopedIntegration(integration.catalogId) &&
    !isKimHelpdesk(integration.catalogId) &&
    !integration.isConnected &&
    !!connectedHelpdesk &&
    connectedHelpdesk !== integration.name;

  const blockedReason = needsBusinessUnit
    ? 'Choose a business unit first'
    : helpdeskTaken
      ? `${connectedHelpdesk} is this account's helpdesk. Disconnect it to switch.`
      : null;

  return (
    <>
      <div className='bg-white border border-gray-500 rounded-2xl p-4 flex flex-col gap-3'>
        {/* Row 1: Logo + Connected badge */}
        <div className='flex items-start justify-between'>
          <div className='size-10 rounded-[11px] overflow-hidden shrink-0'>
            <img src={integration.logo} alt={integration.name} className='size-full object-contain' />
          </div>
          {integration.isConnected && (
            <div className='flex items-center gap-1 bg-green-50 px-1.5 py-1 rounded-full shrink-0'>
              <span className='size-2 rounded-full bg-green-500 shrink-0' />
              <span className='text-xs font-medium text-green-500 leading-none'>Connected</span>
            </div>
          )}
        </div>

        {/* Row 2: Name + Description */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1'>
            <span className='text-sm font-medium text-ink'>{integration.name}</span>
            {integration.isConnected && integration.connectedName && (
              <span className='text-xs text-muted-foreground'>· {integration.connectedName}</span>
            )}
          </div>
          <p className='text-[13px] leading-[18px] text-muted-foreground'>{integration.description}</p>
        </div>

        {/* Row 4: View Actions + Connect/Manage buttons (right-aligned) */}
        <div className='flex justify-end gap-2'>
          <Button
            variant='outline'
            size='sm'
            className='text-[13px] text-gray-900 cursor-pointer hover:bg-gray-50'
            onClick={() => navigate(`/settings/integrations/${integration.catalogId}/actions`)}
          >
            View Actions
          </Button>
          {blockedReason ? (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* A disabled button fires no events, so the tooltip needs a wrapper
                    that can still receive them — otherwise the reason is invisible. */}
                <span tabIndex={0}>
                  <Button variant='outline' size='sm' className='text-[13px] text-gray-900' disabled>
                    Connect
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{blockedReason}</TooltipContent>
            </Tooltip>
          ) : (
            <Button
              variant='outline'
              size='sm'
              className='text-[13px] text-gray-900 cursor-pointer hover:bg-gray-50'
              onClick={() => setIsSheetOpen(true)}
            >
              {integration.isConnected ? 'Manage' : 'Connect'}
            </Button>
          )}
        </div>
      </div>

      <IntegrationSheet integration={integration} isOpen={isSheetOpen} onOpenChange={setIsSheetOpen} />
    </>
  );
};
